import React, { useState, useEffect } from 'react';
import './GroupDetailModal.css';
import { getToken } from '../../../../services/api';

const GroupDetailModal = ({ groupId, estado, onClose, onGroupUpdate }) => {
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [newGroupStatus, setNewGroupStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [showStatusChange, setShowStatusChange] = useState(false);

  useEffect(() => {
    fetchGroupDetails();
  }, [groupId]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      const response = await fetch(`${API_URL}/precatorio-group-status/gestor/group/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar detalhes do grupo');
      }

      const data = await response.json();
      setGroupData(data);
      setNewGroupStatus(data.groupStatus.groupStatus);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setAddingComment(true);
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorio-group-status/gestor/group/${groupId}/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: newComment })
      });

      if (!response.ok) {
        throw new Error('Erro ao adicionar comentário');
      }

      const data = await response.json();
      const newComment = data.comment;
      
      setNewComment('');
      
      // Update parent component with new data
      if (onGroupUpdate) {
        onGroupUpdate({
          _id: groupId,
          groupStatus: groupData.groupStatus.groupStatus,
          commentsCount: groupData.groupStatus.comments.length + 1,
          lastComment: newComment
        });
      }
      
      fetchGroupDetails(); // Refresh data
    } catch (err) {
      alert('Erro ao adicionar comentário: ' + err.message);
    } finally {
      setAddingComment(false);
    }
  };

  const handleUpdateGroupStatus = async () => {
    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorio-group-status/gestor/group/${groupId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          groupStatus: newGroupStatus,
          reason: statusReason 
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status do grupo');
      }

      setShowStatusChange(false);
      setStatusReason('');
      
      // Update parent component with new status
      if (onGroupUpdate) {
        onGroupUpdate({
          _id: groupId,
          groupStatus: newGroupStatus,
          commentsCount: groupData.groupStatus.comments.length,
          lastComment: groupData.groupStatus.comments.length > 0 
            ? groupData.groupStatus.comments[groupData.groupStatus.comments.length - 1]
            : null
        });
      }
      
      fetchGroupDetails(); // Refresh data
    } catch (err) {
      alert('Erro ao atualizar status: ' + err.message);
    }
  };

  const handleUpdatePrecatorioStatus = async (precatorioId, newStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [precatorioId]: true }));
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_VERCEL_API_URL : process.env.REACT_APP_LOCAL_API_URL;
      
      const response = await fetch(`${API_URL}/precatorio-group-status/gestor/precatorio/${estado}/${precatorioId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status do precatório');
      }

      fetchGroupDetails(); // Refresh data
    } catch (err) {
      alert('Erro ao atualizar status: ' + err.message);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [precatorioId]: false }));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDocument = (type, number) => {
    if (type === 'CPF') {
      return number.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (type === 'CNPJ') {
      return number.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return number;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Em Análise':
        return '#f59e0b';
      case 'Contatando':
        return '#3b82f6';
      case 'À Venda':
        return '#8b5cf6';
      case 'Vendida':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Carregando detalhes...</div>
        </div>
      </div>
    );
  }

  if (error || !groupData) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="error">Erro: {error || 'Dados não encontrados'}</div>
        </div>
      </div>
    );
  }

  const { groupStatus, precatorios } = groupData;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Detalhes do Grupo</h2>
            <p className="document-display">
              {groupStatus.documentType}: {formatDocument(groupStatus.documentType, groupStatus.documentNumber)}
            </p>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Group Status Section */}
          <div className="group-status-section">
            <div className="section-header">
              <h3>Status do Grupo</h3>
              <button 
                className="change-status-btn"
                onClick={() => setShowStatusChange(!showStatusChange)}
              >
                {showStatusChange ? 'Cancelar' : 'Alterar Status'}
              </button>
            </div>
            
            {showStatusChange ? (
              <div className="status-change-form">
                <select
                  value={newGroupStatus}
                  onChange={(e) => setNewGroupStatus(e.target.value)}
                  className="status-select"
                >
                  <option value="Não Contatou">Não Contatou</option>
                  <option value="Em Contato">Em Contato</option>
                  <option value="Vendendo">Vendendo</option>
                  <option value="Não Vende">Não Vende</option>
                </select>
                <input
                  type="text"
                  placeholder="Motivo da mudança (opcional)"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  className="reason-input"
                />
                <button 
                  className="save-status-btn"
                  onClick={handleUpdateGroupStatus}
                  disabled={newGroupStatus === groupStatus.groupStatus}
                >
                  Salvar Status
                </button>
              </div>
            ) : (
              <div className="current-status">
                <span className="status-label">Status Atual:</span>
                <span className="status-value" style={{ color: getStatusColor(groupStatus.groupStatus) }}>
                  {groupStatus.groupStatus}
                </span>
              </div>
            )}
          </div>

          {/* Precatorios List */}
          <div className="precatorios-section">
            <h3>Precatórios ({precatorios.length})</h3>
            <div className="precatorios-list">
              {precatorios.map(precatorio => (
                <div key={precatorio._id} className="precatorio-item">
                  <div className="precatorio-info">
                    <div className="precatorio-header">
                      <span className="precatorio-number">{precatorio.numeroPrecatorio}</span>
                      <span className="precatorio-value">{formatCurrency(precatorio.valorAtualizado)}</span>
                    </div>
                    <div className="precatorio-details">
                      <span>Natureza: {precatorio.natureza}</span>
                      <span>Ente: {precatorio.ente}</span>
                      <span>Orçamento: {precatorio.orcamento}</span>
                    </div>
                  </div>
                  <div className="precatorio-status">
                    <select
                      value={precatorio.status}
                      onChange={(e) => handleUpdatePrecatorioStatus(precatorio._id, e.target.value)}
                      disabled={updatingStatus[precatorio._id]}
                      className="precatorio-status-select"
                      style={{ borderColor: getStatusColor(precatorio.status) }}
                    >
                      <option value="Não Contatou">Não Contatou</option>
                      <option value="Em Análise">Em Análise</option>
                      <option value="Contatando">Contatando</option>
                      <option value="À Venda">À Venda</option>
                      <option value="Vendida">Vendida</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <div className="precatorios-summary">
              <span>Valor Total: {formatCurrency(precatorios.reduce((sum, p) => sum + p.valorAtualizado, 0))}</span>
            </div>
          </div>

          {/* Comments Section */}
          <div className="comments-section">
            <h3>Comentários ({groupStatus.comments.length})</h3>
            
            <div className="add-comment">
              <textarea
                placeholder="Adicionar um comentário..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="comment-textarea"
                rows="3"
              />
              <button 
                className="add-comment-btn"
                onClick={handleAddComment}
                disabled={!newComment.trim() || addingComment}
              >
                {addingComment ? 'Adicionando...' : 'Adicionar Comentário'}
              </button>
            </div>

            <div className="comments-list">
              {groupStatus.comments.length === 0 ? (
                <p className="no-comments">Nenhum comentário ainda.</p>
              ) : (
                groupStatus.comments.map((comment, index) => (
                  <div key={index} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-author">{comment.author.userName}</span>
                      <span className="comment-date">
                        {new Date(comment.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <p className="comment-text">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupDetailModal;
